/**
 * PassageToolbars — the passage's marking tools, in one place.
 *
 * The ten marking tools are the primary row — the full set a Singapore primary teacher teaches.
 * The three highlighter pens, which shipped first, fold away behind a "Highlight" toggle: nothing
 * is taken from students already using them, and the row stays one thing rather than two.
 *
 * MUTUAL EXCLUSION lives here rather than in either toolbar. Both tools drive the same pointer
 * gesture on the same words, so exactly one may be armed: picking a shape puts the pen down and
 * picking a pen puts the shape down. Neither child component knows the other exists.
 *
 * Hosts (PassagePanel, PassageCard) render this one component and stay thin.
 */

import { useState } from 'react';
import { Highlighter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PassageAnnotationToolbar } from '@/components/PassageAnnotationToolbar';
import { PassageHighlightToolbar } from '@/components/PassageHighlightToolbar';
import type { HighlightColor } from '@/lib/passageHighlights';
import type { AnnotationShape } from '@/lib/passageAnnotations';

export interface PassageToolbarsProps {
  armedShape: AnnotationShape | null;
  onArmShape: (shape: AnnotationShape | null) => void;
  onUndoMark: () => void;
  onClearMarks: () => void;
  hasMarks: boolean;

  armedColor: HighlightColor | null;
  onArmColor: (color: HighlightColor | null) => void;
  onClearHighlights: () => void;
  hasHighlights: boolean;
}

export function PassageToolbars({
  armedShape,
  onArmShape,
  onUndoMark,
  onClearMarks,
  hasMarks,
  armedColor,
  onArmColor,
  onClearHighlights,
  hasHighlights,
}: PassageToolbarsProps) {
  // Opened by the student, or forced open when a pen is already armed or highlights exist —
  // hiding an armed pen behind a collapsed row would leave the passage in draw mode with no
  // visible reason why.
  const [expanded, setExpanded] = useState(false);
  const showHighlighter = expanded || armedColor !== null || hasHighlights;

  const armShape = (shape: AnnotationShape | null) => {
    onArmShape(shape);
    if (shape) onArmColor(null);
  };

  const armColor = (color: HighlightColor | null) => {
    onArmColor(color);
    if (color) onArmShape(null);
  };

  const highlightToggle = (
    <button
      type="button"
      // The label text is display:none below `sm`, which removes it from the accessibility
      // tree — so the name has to be stated here or the button goes unnamed on a phone.
      aria-label="Highlight"
      aria-expanded={showHighlighter}
      aria-controls="passage-highlight-pens"
      onClick={() => setExpanded((open) => !open)}
      className={cn(
        'flex min-h-11 items-center gap-1.5 rounded-xl px-2.5 font-body text-sm transition-colors',
        showHighlighter
          ? 'bg-otter-sand text-otter-earth-800'
          : 'text-otter-earth-700 hover:bg-otter-sand'
      )}
    >
      <Highlighter aria-hidden="true" className="h-4 w-4" />
      <span className="hidden sm:inline">Highlight</span>
    </button>
  );

  return (
    <div>
      <PassageAnnotationToolbar
        armedShape={armedShape}
        onArm={armShape}
        onUndo={onUndoMark}
        onClear={onClearMarks}
        hasMarks={hasMarks}
        trailing={highlightToggle}
      />

      {showHighlighter && (
        <div id="passage-highlight-pens">
          <PassageHighlightToolbar
            armedColor={armedColor}
            onArm={armColor}
            onClear={onClearHighlights}
            hasHighlights={hasHighlights}
          />
        </div>
      )}
    </div>
  );
}
